import { type PersonType } from '@/stores/models/Person';
import { isMasculine, isFeminine } from './gender';
import { familyStore } from '@/stores/FamilyStore';

export function determineDisplaySpouse(person: PersonType): string | null {
  // Get selected spouse or first spouse
  // Note: Returns familyMemberId since spouseFamilyMemberIds stores familyMemberIds
  let displaySpouseFamilyMemberId = person.spouseFamilyMemberIds[0] || null;

  if (person.selectedSpouseFamilyMemberId) {
    // Find spouse by familyMemberId in FamilyStore
    const spouse = familyStore.getPerson(person.selectedSpouseFamilyMemberId);
    if (spouse) {
      displaySpouseFamilyMemberId = person.selectedSpouseFamilyMemberId;
    }
  }
  return displaySpouseFamilyMemberId;
}

/**
 * Determine initial position assignment for a person and their spouse
 * @param person1 - The person to position
 * @param person2 - The spouse to position
 * @returns Object with leftFamilyMemberId and rightFamilyMemberId
 */
export function determinePositions(
  person1: PersonType | null,
  person2: PersonType | null
): { leftFamilyMemberId: string | null; rightFamilyMemberId: string | null } {
  if (!person1 && !person2) {
    return { leftFamilyMemberId: null, rightFamilyMemberId: null };
  }
  if (!person1 && person2) {
    return { leftFamilyMemberId: person2.familyMemberId, rightFamilyMemberId: null };
  }
  if (!person2 && person1) {
    if (isFeminine(person1.gender)) {
      return { leftFamilyMemberId: null, rightFamilyMemberId: person1.familyMemberId };
    } else {
      return { leftFamilyMemberId: person1.familyMemberId, rightFamilyMemberId: null };
    }
  }

  // Both persons exist at this point
  if (!person1 || !person2) {
    return { leftFamilyMemberId: null, rightFamilyMemberId: null };
  }

  // Heterosexual partnership: Male → LEFT, Female → RIGHT
  if (isMasculine(person1.gender) && isFeminine(person2.gender)) {
    return { leftFamilyMemberId: person1.familyMemberId, rightFamilyMemberId: person2.familyMemberId };
  }
  if (isFeminine(person1.gender) && isMasculine(person2.gender)) {
    return { leftFamilyMemberId: person2.familyMemberId, rightFamilyMemberId: person1.familyMemberId };
  }

  // Same-sex or unknown gender: Alphabetical by full name
  const personFullName = `${person1.firstName} ${person1.lastName}`.toLowerCase();
  const spouseFullName = `${person2.firstName} ${person2.lastName}`.toLowerCase();

  if (personFullName < spouseFullName) {
    return { leftFamilyMemberId: person1.familyMemberId, rightFamilyMemberId: person2.familyMemberId };
  } else {
    return { leftFamilyMemberId: person2.familyMemberId, rightFamilyMemberId: person1.familyMemberId };
  }
}
